import { useState, useRef, useEffect, useCallback } from 'react'
import { Edit2 } from 'lucide-react'
import { GraphEditorModal } from './dashboard/DashboardWidgets'
import { SingleParameterChart } from './SingleParameterChart'
import { ChartErrorBoundary } from './ChartErrorBoundary'

const DEFAULT_COLORS = ['#f59e0b', '#10b981', '#8b5cf6', '#3b82f6', '#ef4444', '#06b6d4']

/**
 * Reusable "Graph" card used by Parameter Comparison and System Performance.
 * Header: title + D/W/M/Y + Start/End date + 3-dot menu (view modes + downloads)
 * + edit pencil (Graph Editor: line color, point shape, Y axis min/max/steps).
 * Body: one SingleParameterChart per selected parameter, stacked.
 *
 * Graph settings persist to localStorage under `storageKey`.
 */
export function DynamicGraphCard({
  title = 'Graph',
  selectedParameters = [],
  dateRange = {},
  timezone = 'UTC',
  activePeriod = 'Y',
  onPeriodClick,
  onDateChange,
  storageKey = 'wq_graph_card',
  colors = DEFAULT_COLORS,
  emptyMessage = 'Select one or more parameters from the sidebar to view them here.',
}) {
  const [showEditor, setShowEditor] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [viewMode, setViewMode] = useState('chart') // 'chart' | 'table' | 'stats'
  const [settings, setSettings] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || 'null')
        || { series: {}, yMin: undefined, yMax: undefined, ySteps: undefined }
    } catch { return { series: {}, yMin: undefined, yMax: undefined, ySteps: undefined } }
  })

  const menuRef = useRef(null)
  const cardRef = useRef(null)
  const dataRef = useRef({})
  const collectData = useCallback((paramId, payload) => { dataRef.current[paramId] = payload }, [])

  useEffect(() => {
    if (!showMenu) return
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [showMenu])

  const saveSettings = (next) => {
    setSettings(next)
    try { localStorage.setItem(storageKey, JSON.stringify(next)) } catch {}
  }

  // ── downloads ───────────────────────────────────────────────────────────
  const _download = (blob, filename) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; a.style.display = 'none'
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 2000)
  }
  const _series = () => selectedParameters.map(p => {
    const d = dataRef.current[p.id] || {}
    return {
      title: `${p.siteName || p.deviceName || ''} | ${p.name}${d.unit ? ` (${d.unit})` : ''}`,
      period: d.period, points: d.points || [],
    }
  })
  const downloadCSV = () => {
    setShowMenu(false)
    try {
      const blocks = _series().map(s => {
        const header = `"Time","${s.title.replace(/"/g, '""')}"`
        const rows = s.points.filter(pt => pt.y != null)
          .map(pt => `"${s.period === 'Y' ? pt.x : new Date(pt.x).toISOString()}",${pt.y}`)
        return [header, ...rows].join('\r\n')
      }).filter(Boolean)
      _download(new Blob(['﻿' + blocks.join('\r\n\r\n')], { type: 'text/csv;charset=utf-8;' }), `${title}.csv`)
    } catch (e) { console.error('[CSV]', e) }
  }
  const downloadXLS = () => {
    setShowMenu(false)
    try {
      const blocks = _series().map(s => {
        const header = `Time\t${s.title}`
        const rows = s.points.filter(pt => pt.y != null)
          .map(pt => `${s.period === 'Y' ? pt.x : new Date(pt.x).toISOString()}\t${pt.y}`)
        return [header, ...rows].join('\r\n')
      })
      _download(new Blob(['﻿' + blocks.join('\r\n\r\n')], { type: 'application/vnd.ms-excel;charset=utf-8;' }), `${title}.xls`)
    } catch (e) { console.error('[XLS]', e) }
  }
  const downloadPNG = () => {
    setShowMenu(false)
    if (viewMode !== 'chart') { setViewMode('chart'); return }
    setTimeout(() => {
      try {
        const svgs = Array.from(cardRef.current?.querySelectorAll('svg.recharts-surface') || [])
        if (svgs.length === 0) return
        const gap = 24
        const widths = svgs.map(s => Math.round(s.getBoundingClientRect().width) || 800)
        const heights = svgs.map(s => Math.round(s.getBoundingClientRect().height) || 220)
        const W = Math.max(...widths)
        const totalH = heights.reduce((a, b) => a + b, 0) + gap * (svgs.length - 1)
        const canvas = document.createElement('canvas')
        canvas.width = W * 2; canvas.height = totalH * 2
        const ctx = canvas.getContext('2d'); ctx.scale(2, 2)
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, W, totalH)
        const offsets = []; let y = 0
        svgs.forEach((_, i) => { offsets.push(y); y += heights[i] + gap })
        let drawn = 0
        svgs.forEach((svg, i) => {
          const clone = svg.cloneNode(true)
          clone.setAttribute('width', widths[i]); clone.setAttribute('height', heights[i])
          clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
          const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' }))
          const img = new Image()
          img.onload = () => {
            ctx.drawImage(img, 0, offsets[i], widths[i], heights[i]); URL.revokeObjectURL(url)
            if (++drawn === svgs.length) canvas.toBlob(b => { if (b) _download(b, `${title}.png`) }, 'image/png')
          }
          img.onerror = () => { URL.revokeObjectURL(url); drawn++ }
          img.src = url
        })
      } catch (e) { console.error('[PNG]', e) }
    }, 100)
  }
  const printCard = () => {
    setShowMenu(false)
    const node = cardRef.current
    if (!node) return
    const win = window.open('', '_blank', 'width=1024,height=768')
    if (!win) { alert('Pop-up blocked. Please allow pop-ups and try again.'); return }
    win.document.write(`<html><head><title>${title}</title></head><body>${node.outerHTML}</body></html>`)
    win.document.close(); win.focus()
    setTimeout(() => win.print(), 300)
  }

  return (
    <div className="bg-white border-2 border-[#358ECD]/30 rounded-lg" ref={cardRef}>
      {/* Header toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-wrap gap-3">
        <h3 className="font-bold text-[#4D4D4D] text-base">{title}</h3>
        <div className="flex items-end gap-3">
          {/* D W M Y */}
          <div className="flex items-center rounded border border-gray-300 overflow-hidden self-center">
            {['D', 'W', 'M', 'Y'].map(p => (
              <button key={p} type="button" onClick={() => onPeriodClick?.(p)}
                className={`px-3 py-1 text-xs font-semibold transition border-r border-gray-300 last:border-r-0 ${
                  activePeriod === p ? 'bg-gray-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}>{p}</button>
            ))}
          </div>
          {/* Dates */}
          <div className="flex flex-col">
            <label className="text-[10px] text-gray-400 leading-none mb-1">Start Date</label>
            <input type="date" value={dateRange.from?.split(' ')[0] || ''}
              onChange={e => onDateChange?.('from', e.target.value + ' 00:00:00')}
              className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:border-gray-400" />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] text-gray-400 leading-none mb-1">End Date</label>
            <input type="date" value={dateRange.to?.split(' ')[0] || ''}
              onChange={e => onDateChange?.('to', e.target.value + ' 23:59:59')}
              className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:border-gray-400" />
          </div>
          {/* 3-dot menu */}
          <div className="relative self-center" ref={menuRef}>
            <button type="button" title="More options" onClick={() => setShowMenu(v => !v)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
              </svg>
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-2xl z-[9999] py-1.5 text-[13px] text-gray-700">
                {[['chart', 'View Chart'], ['table', 'View Data Table'], ['stats', 'View Statistics']].map(([mode, label]) => (
                  <button key={mode} className="w-full text-left px-4 py-2 hover:bg-gray-50 transition flex items-center gap-2.5"
                    onClick={() => { setViewMode(mode); setShowMenu(false) }}>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${viewMode === mode ? 'bg-[#358ECD]' : 'border border-gray-300'}`} />
                    {label}
                  </button>
                ))}
                <div className="my-1.5 border-t border-gray-100" />
                <button className="w-full text-left px-4 py-2 hover:bg-gray-50 transition" onClick={printCard}>Download PDF</button>
                <button className="w-full text-left px-4 py-2 hover:bg-gray-50 transition" onClick={downloadPNG}>Download PNG Image</button>
                <button className="w-full text-left px-4 py-2 hover:bg-gray-50 transition" onClick={downloadCSV}>Download CSV</button>
                <button className="w-full text-left px-4 py-2 hover:bg-gray-50 transition" onClick={downloadXLS}>Download XLS</button>
                <div className="my-1.5 border-t border-gray-100" />
                <button className="w-full text-left px-4 py-2 hover:bg-gray-50 transition" onClick={printCard}>Print Chart</button>
              </div>
            )}
          </div>
          {/* Edit */}
          <button type="button" onClick={() => setShowEditor(true)} title="Edit graph settings"
            className="self-center p-1.5 text-gray-400 hover:text-[#358ECD] hover:bg-gray-100 rounded transition">
            <Edit2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Charts */}
      <div className="p-4 space-y-8">
        {selectedParameters.length === 0 ? (
          <div className="py-10 text-center text-gray-500 text-sm">{emptyMessage}</div>
        ) : (
          selectedParameters.map((param, idx) => {
            const s = settings.series?.[param.id] || {}
            return (
              <ChartErrorBoundary key={param.id} resetKey={`${activePeriod}|${dateRange.from}|${dateRange.to}|${viewMode}|${JSON.stringify(s)}`}>
                <SingleParameterChart
                  param={param}
                  dateRange={dateRange}
                  timezone={timezone}
                  timePeriod={activePeriod}
                  color={s.color || colors[idx % colors.length]}
                  pointShape={s.pointShape || 'Circle'}
                  yMin={settings.yMin}
                  yMax={settings.yMax}
                  ySteps={settings.ySteps}
                  viewMode={viewMode}
                  onData={collectData}
                  embedded
                />
              </ChartErrorBoundary>
            )
          })
        )}
      </div>

      <GraphEditorModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={saveSettings}
        selectedParameters={selectedParameters}
        seriesMetadata={Object.fromEntries(selectedParameters.map(p => [p.id, { name: p.name, siteName: p.siteName, unit: p.unit }]))}
        settings={settings}
        widget={{ id: storageKey, title }}
      />
    </div>
  )
}
